import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TopUpDialog } from './TopUpDialog';
import { BalanceHistoryChart } from './BalanceHistoryChart';
import { Bell, Loader2, RefreshCw, AlertTriangle, CheckCircle, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WalletRow = ({ w }) => (
  <div data-testid={`wallet-alert-${w.id}`} className={`rounded-lg p-3 border ${w.low ? 'border-red-500/40 bg-red-500/10' : 'border-emerald-500/30 bg-emerald-500/5'}`}>
    <div className="flex items-center justify-between">
      <span className="text-sm text-white font-medium">{w.label}</span>
      {w.low
        ? <span className="flex items-center gap-1 text-xs text-red-400" data-testid={`wallet-alert-${w.id}-status`}><AlertTriangle className="w-3 h-3" /> Low</span>
        : <span className="flex items-center gap-1 text-xs text-emerald-400" data-testid={`wallet-alert-${w.id}-status`}><CheckCircle className="w-3 h-3" /> OK</span>}
    </div>
    <code className="text-xs text-purple-300 break-all block mt-1">{w.address}</code>
    <p className="text-xs text-slate-300 mt-2" data-testid={`wallet-alert-${w.id}-balance`}>
      {w.balance_eth.toFixed(6)} ETH (${w.balance_usd.toFixed(2)}) · needs {w.min_eth.toFixed(6)} ETH (${w.min_usd.toFixed(2)}) for {w.purpose}
    </p>
    {w.key_configured === false && (
      <p className="flex items-center gap-1 text-xs text-amber-300 mt-1" data-testid="wallet-alert-funding-key-missing">
        <KeyRound className="w-3 h-3" /> Private key not set: this wallet can't send ETH yet
      </p>
    )}
  </div>
);

export const WalletAlertsCard = ({ token }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [minEth, setMinEth] = useState('');
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/pas-admin/wallet-alerts`, { headers });
      setData(r.data);
      setMinEth(String(r.data.settings.funding_min_eth));
      setRefreshKey(k => k + 1);
    } catch (e) { toast.error('Could not load wallet balances'); }
  };
  useEffect(() => { load(); }, [token]);

  const save = async (values) => {
    try {
      await axios.put(`${API}/pas-admin/wallet-alerts/settings`, values, { headers });
      toast.success('Alert settings saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const checkNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/pas-admin/wallet-alerts/check-now`, {}, { headers });
      toast.success(r.data.emailed ? 'Low balance email sent' : 'All wallets have enough ETH');
      load();
    } catch (e) { toast.error('Check failed'); } finally { setBusy(false); }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl" data-testid="wallet-alerts-card">
      <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-amber-300" />
          <h3 className="text-white font-semibold">Low Balance Alerts (Mainnet)</h3>
        </div>
        <button onClick={load} data-testid="wallet-alerts-refresh-btn" className="p-2 text-slate-400 hover:text-white rounded"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {!data ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 text-purple-400 animate-spin" /></div>
      ) : (
        <div className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-3">{data.wallets.map(w => <WalletRow key={w.id} w={w} />)}</div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={data.settings.enabled} data-testid="wallet-alerts-enabled-checkbox"
                onChange={(e) => save({ enabled: e.target.checked })} />
              Hourly email alerts
            </label>
            <label className="flex items-center gap-2">
              Gas wallet minimum
              <input type="number" step="0.001" min="0.001" value={minEth} onChange={(e) => setMinEth(e.target.value)}
                data-testid="wallet-alerts-funding-min-input"
                className="w-24 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white" /> ETH
            </label>
            <button onClick={() => save({ funding_min_eth: parseFloat(minEth) })} data-testid="wallet-alerts-save-btn"
              className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-white text-xs">Save</button>
            <button onClick={() => setTopUpOpen(true)} data-testid="wallet-topup-btn"
              className="ml-auto px-3 py-1.5 rounded bg-emerald-500 hover:bg-emerald-600 text-white text-xs">Top up deployment wallet</button>
            <button onClick={checkNow} disabled={busy} data-testid="wallet-alerts-check-now-btn"
              className="px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs flex items-center gap-1">
              {busy && <Loader2 className="w-3 h-3 animate-spin" />} Check &amp; email now
            </button>
          </div>
          <BalanceHistoryChart token={token} refreshKey={refreshKey} />
          <TopUpDialog open={topUpOpen} token={token} onClose={() => setTopUpOpen(false)} onDone={() => { setTopUpOpen(false); load(); }} />
          <p className="text-xs text-slate-500" data-testid="wallet-alerts-last-check">
            Last automatic check: {data.last_check ? new Date(data.last_check).toLocaleString() : 'not yet'} · reminders repeat every 24h while low
          </p>
        </div>
      )}
    </div>
  );
};

export default WalletAlertsCard;
